import { useAngularPreventLecturePageNavigation } from '../hooks/use-prevent-lecture-page-navigation';

/* @ngInject */
export default function LiveSessionEditRecordingController(
  $scope,
  $stateParams,
  $timeout,
  $uibModalInstance,
  $window,
  AlertMessages,
  ConfirmationOverlays,
  CurrentCourseManager,
  S3NameSpaces,
  S3UploadFactory,
  StateManager,
  Upload,
  config,
  liveSessionId,
  nvUtil,
  recording,
) {
  const vm = this;

  vm.recording = recording;
  vm.liveSessionId = liveSessionId;
  vm.videoDraft = { ...recording };
  vm.CurrentCourseManager = CurrentCourseManager;
  vm.config = config;
  vm.fileMaxSize = `${config.files.rteFileSizeLimit}MB`;
  vm.thumbnailMaxSize = `${config.files.smallFileSizeLimit}MB`;
  const recordingSizeLimitGb = 6;
  vm.maxVideoFileSize = `${recordingSizeLimitGb}GB`;
  let dismissedByUser = false;

  vm.allowedFileTypes = {
    slides: vm.config.files.rte.presentations.extensions,
    textTranscript: vm.config.files.rte.documents.extensions,
    transcript: vm.config.files.subtitles.split(','),
    customThumbnail: vm.config.files.rte.images.extensions,
    video: vm.config.files.allVideoExtensions,
  };

  vm.hasValidVideoUrl = () => vm.videoDraft.lowQualityUrl || vm.videoDraft.mediumQualityUrl || (vm.videoDraft.type === 'video/mp4' && vm.videoDraft.originalUrl);

  vm.saveFile = ($file, type) => {
    if ($file.name.includes('.')) {
      const extension = $file.name.split('.').pop();

      if (!vm.allowedFileTypes[type].includes(`.${extension.toLowerCase()}`)) {
        vm.videoEditForm.$dirty = false;
        $timeout(() => {
          AlertMessages.error('FORM.OOPS', 'FILE_UPLOAD.NOT_SUPPORTED_ALERT');
        });
        vm.videoDraft[type] = null;
        vm.videoDraft[`${type}FileName`] = null;
      } else {
        vm.videoDraft[type] = $file;
        vm.videoEditForm.$setDirty();
      }
    }
  };

  vm.onDelete = (type, attr) => {
    if (vm.videoDraft[`${type}Url`]) {
      vm.videoDraft[attr] = true;
    }
    vm.videoDraft[type] = null;
    vm.videoDraft[`${type}FileName`] = null;

    vm.videoEditForm.$setDirty();
  };

  vm.updateRecording = (video) => {
    const payload = {};
    ['id', 'isDownloadable', 'uniqueId', 'fileName', 'slides', 'transcript', 'textTranscript', 'customThumbnail', 'removeSlides',
      'removeTranscript', 'removeTextTranscript', 'removeCustomThumbnail', 'numCoursesUsing', 'recentCoursesUsing'].forEach(key => {
      payload[nvUtil.toSnakeCase(key)] = video[key];
    });
    return Upload.upload({
      url: `${$stateParams.catalogId}/live_session/${vm.liveSessionId}/update_video`,
      method: 'POST',
      fields: {
        video: payload,
      },
    }).then((response) => {
      Object.assign(vm.recording, response.data.result.file);
      dismissedByUser = true;
      $uibModalInstance.close();
    }, (response) => {
      // error handling
      AlertMessages.error('FORM.OOPS', 'FORM.ERROR_SOMETHING_WRONG');
    });
  };

  vm.uploadFile = ($file, $invalidFiles) => {
    if ($invalidFiles?.length) {
      if ($invalidFiles[0].$error === 'maxSize') {
        AlertMessages.error('FORM.OOPS', 'FILE_UPLOAD.SIZE_EXCEEDED_ALERT', {}, { sizeLimit: $invalidFiles[0].$errorParam });
      } else {
        AlertMessages.error('FORM.OOPS', 'FILE_UPLOAD.NOT_SUPPORTED_ALERT');
      }
    } else if ($file) {
      let valid = true;

      if ($file.name.includes('.')) {
        const extension = $file.name.split('.').pop();

        if (!vm.allowedFileTypes.video.includes(`.${extension.toLowerCase()}`)) {
          $timeout(() => {
            AlertMessages.error('FORM.OOPS', 'FILE_UPLOAD.NOT_SUPPORTED_ALERT');
          });
          valid = false;
        }
      }

      if (valid && $file.size > recordingSizeLimitGb * 1024 * 1024 * 1024) { // equal to 6GB
        $timeout(() => {
          AlertMessages.error('FORM.OOPS', 'FILE_UPLOAD.SIZE_EXCEEDED_ALERT', {}, { sizeLimit: $file.$errorParam });
        });
        valid = false;
      }

      if (valid) {
        uploadVideoFile($file);
      }
    }
  };

  function uploadVideoFile(file) {
    vm.videoEditForm.$setDirty();
    vm.newVideoS3UploadObj = S3UploadFactory.uploadToS3(file, S3NameSpaces.ATTACHMENTS, true);

    vm.newVideoS3UploadObj.promise.then((response) => {
      Object.assign(vm.videoDraft, response.config.data.file, { fileName: response.config.data.file.name });
      // eslint-disable-next-line no-useless-escape
      vm.videoDraft.name = vm.videoDraft.name.replace(/\.[^\/.]+$/, '');
    }, (response) => {
    });
  }

  function hasUnsavedChanges() {
    return ['url', 'isDownloadable', 'slides', 'textTranscript', 'transcript', 'customThumbnail']
      /* eslint-disable-next-line eqeqeq */
      .reduce((prev, cur) => vm.videoDraft[cur] != recording[cur] || prev, false);
    // We are using `!=` because the videoDraft can have values as null when the user fails a file upload
  }

  const checkUnsavedChanges = () => hasUnsavedChanges();

  useAngularPreventLecturePageNavigation($scope, checkUnsavedChanges);

  // warn if leaving the modal and have unsaved changes
  vm.deregisterStateChangeStart = StateManager.registerStateChangeStart(checkUnsavedChanges,
    'discussions/templates/nv-unsaved-changes-confirmation-overlay.html',
    'FORM.NAVIGATE_AWAY.CLOSE_WINDOW',
    undefined,
    undefined,
    () => {
      vm.deregisterModalClosing?.();
      $uibModalInstance.dismiss();
    });

  vm.deregisterModalClosing = $scope.$on('modal.closing', ($event) => {
    if (hasUnsavedChanges() && !dismissedByUser) {
      $event.preventDefault();

      const modalOverlay = ConfirmationOverlays.openConfirmationModal('lecture_pages/templates/live-session-edit-recording-unsaved-changes-warning-overlay.html');

      modalOverlay.result.then(() => {
        dismissedByUser = true;
        $uibModalInstance.dismiss();
      });
    }
  });

  $scope.$on('$destroy', () => {
    angular.element($window).off('beforeunload');
    vm.deregisterStateChangeStart?.();
  });
}
